/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { isSetToAutoprocess, Oneill_CvAttemptLineItemDto, Oneill_GetStateDto, Oneill_ValidationStateEnum, OneillService } from "app/services/oneill.service";

/** Number of milliseconds between page refresh api calls */
const RefreshTimeout = 1000;

@Component({
  templateUrl: './oneil-portal-validation.dashboard.component.html',
  styleUrls: ['./oneil-portal-validation.dashboard.component.css']
})
export class OneilPortalValidationDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  /** Carton ID Input Field HTML Element Reference */
  @ViewChild('cartonIdField') cartonIdField!: ElementRef;

  /** Portal whose Dashboard is being displayed */
  portal: ServiceInstance;

  /** Current State of the Portal being reflected in the Dashboard */
  currentState: Oneill_GetStateDto;

  /** Carton ID value entered by the User in the UI */
  cartonId: string;

  /** Flag used to indicate whether a User Action is being processed or not.  Used to disable some UI elements and prevent multiple actions from occuring simulatenously. */
  isProcessingAction = false;

  /** Flag used to indicate whether this page was navigated away from.  Used to stop the refresh task. */
  isDestroyed = false;

  /** Setting from the Service that controls whether the Portal is set to Autoprocess after tag reads or not */
  isSetToAutoprocess = false;


  /** Constructor */
  constructor(
    private oneillService: OneillService,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    console.log(`ngOnInit()`);

    // Grab the data from the resolvers
    this.route.data.subscribe(
      (data: {
        portal: ServiceInstance,
        state: Oneill_GetStateDto
      }) => {
        console.log(`Obtained Portal: '${data?.portal?.id}/${data?.portal?.name}'`);
        this.portal = data?.portal;
        this.isSetToAutoprocess = isSetToAutoprocess(this.portal);
        console.log(`Autoprocess? ${this.isSetToAutoprocess}`)

        console.log(`Obtained State: '${JSON.stringify(data?.state)}'`);
        this.currentState = data?.state;
      });

    // Start the "Refresh" Task in the background
    setTimeout(this.refreshData.bind(this), RefreshTimeout);
  }

  /**
   * Destroys the component.
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  /**
   * Returns a boolean indicating if the current State is "Enter Info",
   * meaning it is waiting on the User to scan the Carton.
   */
  isDisplayState_EnterInfo(): boolean {
    return this.currentState?.state == Oneill_ValidationStateEnum.WaitingOnCartonScan;
  }

  /**
   * Returns a string representation for the current State.
   */
  getDisplayState(displayState: Oneill_ValidationStateEnum): string {
    switch(displayState){
      case Oneill_ValidationStateEnum.WaitingOnCartonScan :
        return "Waiting On Barcode Scan...";
      case Oneill_ValidationStateEnum.RetrievingCartonContents :
        return "Retrieving Carton Contents...";
      case Oneill_ValidationStateEnum.CartonBeingValidated :
        return "Validating Carton...";
      case Oneill_ValidationStateEnum.CartonBeingProcessed :
        return "Processing Carton...";
      default:
        return "Unknown State!!!";
    }
  }
  
  /**
   * Task which is continously executing in the background while the component
   * is displayed.  Queries for the current state of the Portal and updates
   * the displayed values. 
   * 
   * This also sets the focus to the "Carton ID" Input Field so that it
   * keeps focus so that the USB Barcode Scanner works as expected.
   */
  private async refreshData(): Promise<void> {
    if (!this.isDestroyed) {
      try {
        // Query for / Update the State
        const state = await this.oneillService.getState(this.portal.id);
        this.setState(state);
      } finally {
        // Focus on the "Carton ID" Input Field if available
        if (this.cartonIdField?.nativeElement != null) {
          console.log("Focusing Carton ID");
          this.cartonIdField.nativeElement.focus();
        }

        // Reschedule the "Refresh" Task if the component hasn't been destroyed.
        if (!this.isDestroyed) {
          setTimeout(this.refreshData.bind(this), RefreshTimeout);
        }
      }
    }
  }

  /**
   * Sets the current state being displayed to the User
   */
  private setState(state: Oneill_GetStateDto): void {
    // Only reflect a state change if it actually changes
    if (JSON.stringify(this.currentState) != JSON.stringify(state)) {
      this.currentState = state;
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Called when the "Enter" Key Up Event is called on the "Carton ID" Input Field.
   * This calls the "Enter Info" Function on the Portal in the background.
   */
  enterCartonId(): void {
    console.log(`enterCartonId()`);
    if (!this.isProcessingAction) {
      this.isProcessingAction = true;
      this._enterCartonId();
    }
  }

  /**
   * Background Task for handling calling the "Enter Info" Function on the Portal.
   * Calls the Function to load the Carton Information to be validated, then
   * updates the screen. 
   */
  private async _enterCartonId(): Promise<void> {
    console.log(`enterCartonId() - Carton ID: '${this.cartonId}'`);
    if (this.cartonId?.length > 0) {

      // Call the "Enter Info" API
      let state: Oneill_GetStateDto = null;
      try {
        state = await this.oneillService.enterCartonId(this.portal.id, this.cartonId);
      } catch (error) {
        this.handleError(error);
        this.cartonId = "";
        this.isProcessingAction = false;
        this.changeDetectorRef.detectChanges();
        return;
      }

      // Update the state if one was retrieved, otherwise let the "Refresh" Task take care of it
      if (state != null) {
        this.setState(state);
      }

      // Update the UI
      this.cartonId = "";
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
      return;
    } else {
      // TODO: Display error stating that the Carton ID is missing
      this.cartonId = "";
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
      return;
    }
  }

  /**
   * Called when the "Clear Carton" UI Button is clicked.
   * Calls the "Clear Carton" Function on the Portal in the background.
   */
  onClearCartonClicked(): void {
    console.log(`onClearCartonClicked()`);
    if (!this.isProcessingAction) {
      this.isProcessingAction = true;
      this._clearCarton();
    }
  }

  /**
   * Background Task for calling the "Clear Carton" Function on the Portal.
   * Calls the API and updates the UI if needed.
   */
  private async _clearCarton(): Promise<void> {
    console.log(`_clearCarton()'`);

    // Call the "Clear Carton" API"
    let state: Oneill_GetStateDto = null;
    try {
      state = await this.oneillService.clearCarton(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.cartonId = "";
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Update the state if one was retrieved (if not let the "Refresh" Task handle it)
    if (state != null) {
      this.setState(state);
    }

    // Update the UI
    this.cartonId = "";
    this.isProcessingAction = false;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Called when the "Process Carton" UI Button is clicked.
   * Calls the "Process Carton" Function on the portal in the background.
   */
  onProcessCartonClicked(): void {
    console.log(`onProcessCartonClicked()`);
    if (!this.isProcessingAction) {
      this.isProcessingAction = true;
      this._processCarton();
    }
  }

  /**
   * Background Task for calling the "Process Carton" Function on the portal.
   * Calls the API then updates the UI if needed.
   */
  private async _processCarton(): Promise<void> {
    console.log(`_processCarton()'`);

    // Call the "Process Carton" API
    let state: Oneill_GetStateDto = null;
    try {
      state = await this.oneillService.processCarton(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.cartonId = "";
      this.isProcessingAction = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Update the state if one was retrieved (if not let the "Refresh" Task handle it)
    if (state != null) {
      this.setState(state);
    }

    // Update the UI
    this.cartonId = "";
    this.isProcessingAction = false;
    this.changeDetectorRef.detectChanges();
  }



  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being in error. (RED)
   */
  isErrorRow(lineItem: Oneill_CvAttemptLineItemDto): boolean {
    return lineItem?.scanned_quantity > lineItem?.required_quantity && lineItem?.required_quantity > 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighted as
   * being unknown. (GRAY)
   */
  isUnknownRow(lineItem: Oneill_CvAttemptLineItemDto): boolean {
    return lineItem?.required_quantity == 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being completed successfully. (GREEN)
   */
  isSuccessRow(lineItem: Oneill_CvAttemptLineItemDto): boolean {
    return lineItem?.scanned_quantity == lineItem?.required_quantity;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * partially fulfilled. (YELLOW)
   */
  isPartialRow(lineItem: Oneill_CvAttemptLineItemDto): boolean {
    return (lineItem?.scanned_quantity >= 0 && lineItem?.required_quantity > 0 && lineItem?.required_quantity > lineItem?.scanned_quantity);
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    if (error?.error != null) {
      //alert(error.error);
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }

    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}
